import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, of, catchError, tap, switchMap, map } from 'rxjs';
import { Router } from '@angular/router';
import { UserModel } from '../models/app.models';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private currentUserSubject = new BehaviorSubject<UserModel | null>(null);
  public currentUser$ = this.currentUserSubject.asObservable();
  
  private isInitializedSubject = new BehaviorSubject<boolean>(false);
  public isInitialized$ = this.isInitializedSubject.asObservable();

  constructor(private http: HttpClient, private router: Router) {
  }

  public get currentUser(): UserModel | null {
    return this.currentUserSubject.value;
  }

  /** Poslední přihlášený e-mail — pro předvyplnění lock screenu. Přežívá odhlášení. */
  public get lastEmail(): string | null {
    return localStorage.getItem('last_email');
  }

  public get isInitialized(): boolean {
    return this.isInitializedSubject.value;
  }

  public initAuth(): Observable<boolean> {
    const token = localStorage.getItem('token');
    if (!token) {
      this.isInitializedSubject.next(true);
      return of(false);
    }

    return this.fetchUserInfo().pipe(
      map(user => !!user),
      tap(() => this.isInitializedSubject.next(true)),
      catchError(() => {
        this.isInitializedSubject.next(true);
        return of(false);
      })
    );
  }

  login(email: string, password: string): Observable<UserModel | null> {
    const body = new URLSearchParams();
    body.set('grant_type', 'password');
    body.set('username', email);
    body.set('password', password);
    body.set('scope', 'offline_access');

    const headers = new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded');

    return this.http.post<any>('/connect/token', body.toString(), { headers }).pipe(
      tap(response => {
        this.storeTokens(response);
        localStorage.setItem('last_email', email); // pro předvyplnění lock screenu
      }),
      switchMap(() => this.fetchUserInfo())
    );
  }

  /**
   * Obnoví access token pomocí uloženého refresh tokenu (grant_type=refresh_token).
   * Volá ho AuthInterceptor při 401, aby krátká nečinnost uživatele neodhlásila.
   * OpenIddict používá rolling refresh tokeny — každé obnovení vrátí nový refresh
   * token, proto ukládáme oba znovu. Vrací true při úspěchu.
   */
  refreshToken(): Observable<boolean> {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      return of(false);
    }

    const body = new URLSearchParams();
    body.set('grant_type', 'refresh_token');
    body.set('refresh_token', refreshToken);
    body.set('scope', 'offline_access');

    const headers = new HttpHeaders().set('Content-Type', 'application/x-www-form-urlencoded');

    return this.http.post<any>('/connect/token', body.toString(), { headers }).pipe(
      tap(response => this.storeTokens(response)),
      map(response => !!response?.access_token),
      catchError(() => of(false))
    );
  }

  private storeTokens(response: any): void {
    if (response?.access_token) {
      localStorage.setItem('token', response.access_token);
    }
    if (response?.refresh_token) {
      localStorage.setItem('refresh_token', response.refresh_token);
    }
  }

  logout() {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    this.currentUserSubject.next(null);
    this.router.navigate(['/auth/login']);
  }

  isLoggedIn(): boolean {
    return !!this.currentUserSubject.value;
  }

  changePassword(currentPassword: string, newPassword: string): Observable<void> {
    return this.http.post<void>('/api/account/password', { currentPassword, newPassword });
  }

  fetchUserInfo(): Observable<UserModel | null> {
    // Endpoint na backendu, kde se token dešifruje
    return this.http.get<UserModel>('/api/account/userinfo').pipe(
      tap(user => {
        this.currentUserSubject.next(user);
      }),
      catchError(err => {
        console.error('Failed to fetch user info', err);
        return of(null);
      })
    );
  }
}
